import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { MutableRefObject, ReactElement, ReactNode } from 'react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ArrowClockwise as RetryIcon } from '@phosphor-icons/react/dist/ssr/ArrowClockwise'
import { CopySimple as CopyIcon } from '@phosphor-icons/react/dist/ssr/CopySimple'
import { DownloadSimple as DownloadIcon } from '@phosphor-icons/react/dist/ssr/DownloadSimple'
import { Monitor as MonitorIcon } from '@phosphor-icons/react/dist/ssr/Monitor'
import { SpeakerSimpleHigh as SpeakerIcon } from '@phosphor-icons/react/dist/ssr/SpeakerSimpleHigh'
import { SpeakerSimpleX as SpeakerStopIcon } from '@phosphor-icons/react/dist/ssr/SpeakerSimpleX'
import { ThumbsDown as ThumbsDownIcon } from '@phosphor-icons/react/dist/ssr/ThumbsDown'
import { ThumbsUp as ThumbsUpIcon } from '@phosphor-icons/react/dist/ssr/ThumbsUp'
import { marked } from 'marked'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { extractCabinetMapFromToolCalls } from '@/lib/assistant-cabinet-heatmap'
import { assistantCitationHref, type AssistantCitationLink } from '@/lib/assistant-citations'
import { ASSISTANT_TABLE_IMAGE_PX, prepareAssistantMarkdownHtml } from '@/lib/assistant-markdown-images'
import { linkifyAssistantCitations } from '@/lib/assistant-markdown-links'
import { trimProseForOpsDigest } from '@/lib/assistant-message-prose'
import { canRetryAssistantMessage } from '@/lib/assistant-message-utils'
import {
	extractAssistantInternalPath,
	normalizeAssistantNavigatePath,
	sanitizeAssistantMarkdownHrefs,
} from '@/lib/assistant-navigate'
import { extractOpsDigestFromToolCalls } from '@/lib/assistant-ops-digest'
import { formatPageContextLabel } from '@/lib/assistant-page-context'
import { speakAssistantText, stopAssistantSpeech, useAssistantSpeechState } from '@/lib/assistant-speech'
import { formatDataSourceFooter } from '@/lib/assistant-tool-metadata'
import { extractAssistantWidgetsFromToolCalls } from '@/lib/assistant-widgets'
import { chipThumbnailUrl } from '@/lib/images'
import type { ChatMessage } from '@/contexts/chat-context'
import { useChat } from '@/contexts/chat-context'

import { ChatAssistantWidget } from './chat-assistant-widgets'
import { ChatCabinetHeatmap } from './chat-cabinet-heatmap'
import { ChatChart } from './chat-chart'
import { ChatOpsDigest } from './chat-ops-digest'
import { ProductSwapCard } from './product-swap-card'
import { PromotionFormWidget } from './promotion-form-widget'
import { ThinkingIndicator, ThinkingLine } from './thinking-indicator'
import { ToolProgress } from './tool-progress'

interface ChatMessageBubbleProps {
	message: ChatMessage
}

// transitions.dev "icon swap" — both icons live in the same grid cell;
// the inactive one fades out through a small blur + scale-down.
function SwapIcons({
	active,
	from,
	to,
	toHiddenTransform = 'scale(0.25)',
}: {
	active: boolean
	from: React.ReactNode
	to: React.ReactNode
	toHiddenTransform?: string
}) {
	const hidden = (transform: string) => ({ filter: 'blur(2px)', opacity: 0, transform })
	return (
		<Box
			sx={{
				display: 'inline-grid',
				'& > span': {
					display: 'inline-flex',
					gridArea: '1 / 1',
					transition: 'opacity 250ms ease-in-out, filter 250ms ease-in-out, transform 250ms ease-in-out',
					willChange: 'opacity, filter, transform',
					'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
				},
			}}
		>
			<Box component="span" sx={active ? hidden('scale(0.25)') : undefined}>
				{from}
			</Box>
			<Box component="span" sx={active ? undefined : hidden(toHiddenTransform)}>
				{to}
			</Box>
		</Box>
	)
}

// Stroke-drawn check (phosphor paths are fills, which can't draw) — the
// dash offset tweens in when `on` flips, like the success-check recipe.
function StrokeCheck({ on }: { on: boolean }) {
	return (
		<Box component="svg" sx={{ display: 'block', height: 16, overflow: 'visible', width: 16 }} viewBox="0 0 16 16">
			<Box
				component="path"
				d="M3 8.5 l3.5 3.5 L13 4.5"
				sx={{
					fill: 'none',
					stroke: 'currentColor',
					strokeDasharray: 20,
					strokeDashoffset: on ? 0 : 20,
					strokeLinecap: 'round',
					strokeLinejoin: 'round',
					strokeWidth: 2,
					transition: 'stroke-dashoffset 500ms cubic-bezier(0.22, 1, 0.36, 1) 80ms',
					'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
				}}
			/>
		</Box>
	)
}

// transitions.dev "streaming text" — each newly arrived word resolves
// through opacity + blur. Gap is capped so large bursts don't lag behind.
const STREAM_WORD_GAP_MS = 60
const STREAM_WORD_MAX_DELAY_MS = 480

// Widget entrances: a small stagger when several mount in the same commit.
const WIDGET_STAGGER_MS = 80
const WIDGET_STAGGER_MAX_MS = 240

// Every chart/widget animates exactly once — on mount. The stagger delay is
// assigned from a per-message sequence at first render and never derived
// from the node's array position, so a later widget inserting ahead of an
// existing one can't change its animation shorthand and replay it.
function WidgetEntrance({ seq, children }: { seq: MutableRefObject<number>; children: ReactNode }) {
	const orderRef = useRef<number | undefined>(undefined)
	if (orderRef.current === undefined) orderRef.current = seq.current++
	const delay = Math.min(orderRef.current * WIDGET_STAGGER_MS, WIDGET_STAGGER_MAX_MS)
	return (
		<Box
			sx={{
				animation: `assistantWidgetIn 350ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms both`,
				'@keyframes assistantWidgetIn': {
					from: { opacity: 0, transform: 'translateY(16px) scale(0.97)', filter: 'blur(2px)' },
					to: { opacity: 1, transform: 'translateY(0) scale(1)', filter: 'blur(0)' },
				},
				'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
			}}
		>
			{children}
		</Box>
	)
}

const NEGATIVE_FEEDBACK_REASONS = ['Wrong data', 'Too slow', "Didn't understand"] as const
const ASKED_FROM_LABEL_MAX = 40
/** Citation chips shown before "Show N more" — keeps long ranking lists from dominating the bubble. */
const CITATIONS_VISIBLE_COLLAPSED = 5
const NAVIGATE_VISIBLE_COLLAPSED = 5

function citationKey(citation: NonNullable<ChatMessage['citations']>[number]): string {
	if (citation.type === 'cabinet' && citation.siteId) return `cabinet:${citation.siteId}`
	return `${citation.type}:${citation.id}`
}

function citationHref(citation: AssistantCitationLink): string {
	return assistantCitationHref(citation)
}

function citationLabel(citation: NonNullable<ChatMessage['citations']>[number]): string {
	if (citation.title) return citation.title
	if (citation.type === 'store') return `Store #${citation.id}`
	if (citation.type === 'transaction') return `Transaction #${citation.id}`
	if (citation.type === 'cabinet') return citation.siteId ? `Cabinet` : `Cabinet #${citation.id}`
	return `Product #${citation.id}`
}

function truncateCitationLabel(label: string, maxLen = 36): string {
	if (label.length <= maxLen) return label
	return `${label.slice(0, maxLen - 1).trim()}…`
}

const CITATION_THUMB_PX = 18

function citationChipAvatar(citation: NonNullable<ChatMessage['citations']>[number]) {
	if (!citation.imageUrl) return undefined
	if (citation.type !== 'product' && citation.type !== 'store' && citation.type !== 'cabinet') return undefined
	return (
		<Avatar
			src={chipThumbnailUrl(citation.imageUrl, CITATION_THUMB_PX)}
			sx={{
				width: CITATION_THUMB_PX,
				height: CITATION_THUMB_PX,
				'& .MuiAvatar-img': { objectFit: 'contain' },
			}}
		/>
	)
}

function CitationChip({ citation }: { citation: NonNullable<ChatMessage['citations']>[number] }) {
	const fullLabel = citationLabel(citation)
	const label = truncateCitationLabel(fullLabel)
	const chip = (
		<Chip
			component={RouterLink}
			clickable
			avatar={citationChipAvatar(citation)}
			label={label}
			size="small"
			to={citationHref(citation)}
			variant="outlined"
			sx={
				citation.imageUrl
					? {
							pl: 0.25,
							'& .MuiChip-avatar': {
								width: CITATION_THUMB_PX,
								height: CITATION_THUMB_PX,
								ml: 0.5,
							},
						}
					: undefined
			}
		/>
	)

	if (label === fullLabel) return chip

	return (
		<Tooltip title={fullLabel}>
			<Box component="span">{chip}</Box>
		</Tooltip>
	)
}

function CitationChipList({ citations }: { citations: NonNullable<ChatMessage['citations']> }) {
	const [expanded, setExpanded] = useState(false)
	const shouldCollapse = citations.length > CITATIONS_VISIBLE_COLLAPSED
	const visible = shouldCollapse && !expanded ? citations.slice(0, CITATIONS_VISIBLE_COLLAPSED) : citations
	const hiddenCount = citations.length - CITATIONS_VISIBLE_COLLAPSED

	return (
		<Stack spacing={0.25} sx={{ mt: 1 }}>
			<Stack direction="row" flexWrap="wrap" spacing={0.5}>
				{visible.map((citation) => (
					<CitationChip key={citationKey(citation)} citation={citation} />
				))}
			</Stack>
			{shouldCollapse ? (
				<Button
					onClick={() => setExpanded((v) => !v)}
					size="small"
					sx={{ alignSelf: 'flex-start', minWidth: 0, px: 0.5, py: 0, fontSize: '0.75rem' }}
					variant="text"
				>
					{expanded ? 'Show fewer' : `Show ${hiddenCount} more`}
				</Button>
			) : null}
		</Stack>
	)
}

function navigateChipPath(rawPath: string): string {
	return normalizeAssistantNavigatePath(extractAssistantInternalPath(rawPath) ?? rawPath)
}

function NavigateChipList({ actions }: { actions: NonNullable<ChatMessage['navigateActions']> }) {
	const [expanded, setExpanded] = useState(false)
	const shouldCollapse = actions.length > NAVIGATE_VISIBLE_COLLAPSED
	const visible = shouldCollapse && !expanded ? actions.slice(0, NAVIGATE_VISIBLE_COLLAPSED) : actions
	const hiddenCount = actions.length - NAVIGATE_VISIBLE_COLLAPSED

	return (
		<Stack spacing={0.25} sx={{ mt: 1 }}>
			<Stack direction="row" flexWrap="wrap" spacing={0.5}>
				{visible.map((action) => (
					<Chip
						key={action.path}
						component={RouterLink}
						clickable
						color="primary"
						label={action.label}
						size="small"
						to={navigateChipPath(action.path)}
						variant="outlined"
					/>
				))}
			</Stack>
			{shouldCollapse ? (
				<Button
					onClick={() => setExpanded((v) => !v)}
					size="small"
					sx={{ alignSelf: 'flex-start', minWidth: 0, px: 0.5, py: 0, fontSize: '0.75rem' }}
					variant="text"
				>
					{expanded ? 'Show fewer' : `Show ${hiddenCount} more`}
				</Button>
			) : null}
		</Stack>
	)
}

// Configure marked for GFM tables, no mangle/headerIds
marked.setOptions({ gfm: true, breaks: true })

/**
 * Complete table blocks in streamed markdown. A block counts as complete
 * once non-table content follows it (or the stream has ended) — a trailing
 * run of `|` lines may still be growing, so it is held back until then.
 * Returned strings are stable across chunks, so widgets parsing them
 * never re-render mid-stream.
 */
function extractCompleteTables(content: string, isStreaming: boolean): string[] {
	const lines = content.split('\n')
	const tables: string[] = []
	let i = 0
	while (i < lines.length) {
		if (!lines[i].trim().startsWith('|')) {
			i += 1
			continue
		}
		let end = i
		while (end + 1 < lines.length && lines[end + 1].trim().startsWith('|')) end += 1
		const isTailBlock = lines.slice(end + 1).every((l) => l.trim() === '')
		if (!isTailBlock || !isStreaming) {
			const block = lines.slice(i, end + 1)
			if (block.length >= 3) tables.push(block.join('\n'))
		}
		i = end + 1
	}
	return tables
}

export function ChatMessageBubble({ message }: ChatMessageBubbleProps) {
	const navigate = useNavigate()
	const { submitFeedback, resendMessage, isLoading } = useChat()
	const {
		isSpeaking: isSpeechPlaying,
		isLoading: isSpeechLoading,
		messageId: speakingMessageId,
	} = useAssistantSpeechState()
	const isSpeaking = (isSpeechPlaying || isSpeechLoading) && speakingMessageId === message.id
	const [showReasonPicker, setShowReasonPicker] = useState(false)
	const [copied, setCopied] = useState(false)
	const isUser = message.role === 'user'
	const askedFromLabelRaw = isUser && message.pageContext ? formatPageContextLabel(message.pageContext) : null
	const askedFromTruncated = Boolean(askedFromLabelRaw && askedFromLabelRaw.length > ASKED_FROM_LABEL_MAX)
	const askedFromLabel =
		askedFromLabelRaw && askedFromTruncated
			? `${askedFromLabelRaw.slice(0, ASKED_FROM_LABEL_MAX).trimEnd()}…`
			: askedFromLabelRaw
	const isAssistant = message.role === 'assistant'
	const showFeedback =
		isAssistant && !message.isStreaming && Boolean(message.content) && !message.content.startsWith('Error:')
	const canRetry = canRetryAssistantMessage(message)
	// Single-line user bubbles read as pills; wrapped/multi-line ones get a
	// smaller uniform radius so the ends don't bow out like a capsule.
	const isShortUserLine = isUser && !message.content.includes('\n') && message.content.length <= 48
	const hasTable = isAssistant && /\|.+\|/.test(message.content ?? '')
	// One stable string per finished table — charts mount progressively as
	// each table completes and never re-parse, so their entrance animation
	// (and recharts') plays exactly once.
	const completedTables = useMemo(
		() => (isAssistant && message.content ? extractCompleteTables(message.content, Boolean(message.isStreaming)) : []),
		[isAssistant, message.content, message.isStreaming]
	)
	const cabinetHeatmap = useMemo(
		() => (isAssistant ? extractCabinetMapFromToolCalls(message.toolCalls) : null),
		[isAssistant, message.toolCalls]
	)
	const opsDigest = useMemo(
		() => (isAssistant ? extractOpsDigestFromToolCalls(message.toolCalls) : null),
		[isAssistant, message.toolCalls]
	)
	const assistantWidgets = useMemo(
		() => (isAssistant ? extractAssistantWidgetsFromToolCalls(message.toolCalls) : []),
		[isAssistant, message.toolCalls]
	)

	// Visual nodes wait for the text stream to finish — widgets, charts and
	// digest cards mount only once `isStreaming` flips false so nothing pops
	// in under text that's still being written. Each node is keyed stably
	// (tool call id for widgets) so nothing remounts or replays an entrance.
	const widgetNodes = useMemo<ReactElement[]>(() => {
		if (!isAssistant) return []
		const nodes = completedTables.map((block, i) => <ChatChart key={`chart-${i}`} content={block} />)
		if (cabinetHeatmap) nodes.push(<ChatCabinetHeatmap key="map" data={cabinetHeatmap} />)
		if (opsDigest) nodes.push(<ChatOpsDigest key="digest" data={opsDigest} />)
		for (const widget of assistantWidgets) {
			nodes.push(<ChatAssistantWidget key={`widget-${widget.id}`} widget={widget} />)
		}
		return nodes
	}, [isAssistant, completedTables, cabinetHeatmap, opsDigest, assistantWidgets])
	const entranceSeqRef = useRef(0)
	// Everything rendered below the streamed text — widgets, citation and
	// navigation chips, draft actions — waits for the stream to end.
	const showPostContent = !isUser && !message.isStreaming

	// Widgets hold until the stream ends, so only a chart still waiting on
	// its table gets a narration line.
	const queuedNarrations = hasTable && completedTables.length === 0 ? ['Creating chart…'] : []

	// Tables still streaming in are held back — a "Building table…" shimmer
	// line sits where the table will land, and text after it hasn't arrived
	// yet anyway (the stream is sequential). Completed tables get a
	// t-table-in class only on first appearance so re-renders from later
	// chunks don't replay their entrance animation.
	const seenTablesRef = useRef(0)

	const { renderedHtml, tableCount, buildingTable } = useMemo(() => {
		if (!message.content || isUser) return { renderedHtml: '', tableCount: 0, buildingTable: false }
		let markdown = opsDigest ? trimProseForOpsDigest(message.content, opsDigest.summary) : message.content
		markdown = sanitizeAssistantMarkdownHrefs(markdown)
		if (message.citations?.length) {
			markdown = linkifyAssistantCitations(markdown, message.citations)
		}
		let buildingTable = false
		if (message.isStreaming) {
			const lines = markdown.split('\n')
			let end = lines.length - 1
			while (end >= 0 && !lines[end].trim()) end -= 1
			if (end >= 0 && lines[end].trim().startsWith('|')) {
				let start = end
				while (start > 0 && lines[start - 1].trim().startsWith('|')) start -= 1
				markdown = lines.slice(0, start).join('\n')
				buildingTable = true
			}
		}
		let html = prepareAssistantMarkdownHtml(markdown, (md) => marked.parse(md) as string)
		let tableIndex = 0
		const seen = seenTablesRef.current
		html = html.replace(/<table(?=[\s>])/gi, () => {
			const mark = tableIndex >= seen ? ' class="t-table-in"' : ''
			tableIndex += 1
			return `<table${mark}`
		})
		return { renderedHtml: html, tableCount: tableIndex, buildingTable }
	}, [message.content, message.citations, message.isStreaming, isUser, opsDigest])

	useEffect(() => {
		seenTablesRef.current = tableCount
	}, [tableCount])

	// Streaming text: React replaces the innerHTML on every chunk, so this
	// re-wraps all words each update. It must run in a layout effect —
	// otherwise each chunk paints unwrapped for a frame before the spans
	// land, and the tail text visibly flashes/fades again on every token.
	// Previously revealed words get animation:none so they don't replay; when
	// a chunk extends the last revealed word, only its new suffix animates.
	const contentRef = useRef<HTMLDivElement>(null)
	const revealedRef = useRef({ words: 0, lastWordLength: 0 })

	useLayoutEffect(() => {
		if (!isAssistant || !message.isStreaming) {
			revealedRef.current = { words: 0, lastWordLength: 0 }
			return
		}
		const root = contentRef.current
		if (!root) return

		const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
		const textNodes: Text[] = []
		let node: Node | null
		while ((node = walker.nextNode())) textNodes.push(node as Text)

		const { words: revealed, lastWordLength: revealedTailLength } = revealedRef.current
		const makeWordSpan = (text: string, delayMs: number | null) => {
			const span = document.createElement('span')
			span.className = 't-stream-w'
			if (delayMs == null) span.style.animation = 'none'
			else span.style.animationDelay = `${delayMs}ms`
			span.textContent = text
			return span
		}
		let wordIndex = 0
		let lastWordLength = 0
		for (const textNode of textNodes) {
			const text = textNode.nodeValue ?? ''
			if (!text.trim()) continue
			const frag = document.createDocumentFragment()
			for (const part of text.split(/(\s+)/)) {
				if (!part) continue
				if (/^\s+$/.test(part)) {
					frag.appendChild(document.createTextNode(part))
					continue
				}
				if (wordIndex === revealed - 1 && part.length > revealedTailLength) {
					frag.appendChild(makeWordSpan(part.slice(0, revealedTailLength), null))
					frag.appendChild(makeWordSpan(part.slice(revealedTailLength), 0))
				} else if (wordIndex < revealed) {
					frag.appendChild(makeWordSpan(part, null))
				} else {
					frag.appendChild(
						makeWordSpan(part, Math.min((wordIndex - revealed) * STREAM_WORD_GAP_MS, STREAM_WORD_MAX_DELAY_MS))
					)
				}
				lastWordLength = part.length
				wordIndex++
			}
			textNode.parentNode?.replaceChild(frag, textNode)
		}
		revealedRef.current = { words: wordIndex, lastWordLength }
	}, [renderedHtml, message.isStreaming, isAssistant])

	const handleContentClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const target = e.target as HTMLElement
			const anchor = target.closest('a')
			if (!anchor) return
			const href = anchor.getAttribute('href')
			if (!href) return
			const internalPath = extractAssistantInternalPath(href)
			if (internalPath) {
				e.preventDefault()
				navigate(internalPath)
			}
		},
		[navigate]
	)

	const handleThumbsUp = useCallback(() => {
		setShowReasonPicker(false)
		void submitFeedback(message.serverId ?? message.id, 1)
	}, [message.id, message.serverId, submitFeedback])

	const handleThumbsDown = useCallback(() => {
		void submitFeedback(message.serverId ?? message.id, -1)
		setShowReasonPicker(true)
	}, [message.id, message.serverId, submitFeedback])

	const handleCopy = useCallback(() => {
		if (message.content) {
			void navigator.clipboard.writeText(message.content)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		}
	}, [message.content])

	const handleSpeak = useCallback(async () => {
		if (!message.content) return
		if (isSpeaking) {
			stopAssistantSpeech()
			return
		}
		try {
			await speakAssistantText(message.content, { messageId: message.id })
		} catch {
			// TTS is optional — fail silently
		}
	}, [message.content, message.id, isSpeaking])

	const handleExportCsv = useCallback(() => {
		if (!message.content) return
		const lines = message.content.split('\n').filter((l) => l.trim().startsWith('|'))
		if (lines.length < 2) return
		const csvRows = lines
			.filter((l) => !l.match(/^\|[\s-:|]+\|$/))
			.map((l) =>
				l
					.split('|')
					.slice(1, -1)
					.map((cell) => cell.trim())
					.join(',')
			)
		const csv = csvRows.join('\n')
		const blob = new Blob([csv], { type: 'text/csv' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = 'export.csv'
		a.click()
		URL.revokeObjectURL(url)
	}, [message.content])

	const handleReasonSelect = useCallback(
		(reason: string) => {
			void submitFeedback(message.serverId ?? message.id, -1, reason)
			setShowReasonPicker(false)
		},
		[message.id, message.serverId, submitFeedback]
	)

	const handleRetry = useCallback(() => {
		void resendMessage(message.id)
	}, [message.id, resendMessage])

	return (
		<Stack
			spacing={0.5}
			sx={{
				alignItems: isUser ? 'flex-end' : 'flex-start',
				px: 2,
				py: 0.5,
			}}
		>
			<Box
				sx={{
					maxWidth: isUser ? '85%' : '100%',
					// Assistant bubbles span the full chat column — shrink-to-fit
					// leaves charts and widget rows without a stable width, so
					// plots squeeze against the axis and row actions can't reach
					// a consistent right edge.
					width: isUser ? 'auto' : '100%',
					px: isUser ? 1.5 : 0,
					py: isUser ? 1 : 0.5,
					borderRadius: isUser ? (isShortUserLine ? '16px' : '10px') : 0,
					bgcolor: isUser ? 'primary.main' : 'transparent',
					color: isUser ? 'primary.contrastText' : 'text.primary',
				}}
			>
				{!isUser && message.toolCalls?.length ? <ToolProgress toolCalls={message.toolCalls} /> : null}
				{message.content ? (
					isUser ? (
						<Typography sx={{ fontWeight: 400, letterSpacing: '-0.02em', WebkitTextStroke: '0.3px' }} variant="body2">
							{message.content}
						</Typography>
					) : (
						<Box
							sx={{
								fontWeight: 400,
								// 'pretty' rebalances lines on every streamed token —
								// apply it only once the message is complete so the
								// text doesn't keep reflowing while it types.
								textWrap: message.isStreaming ? 'wrap' : 'pretty',
								'& p': { m: 0, mb: 1 },
								'& p:last-child': { mb: 0 },
								'& ul, & ol': { m: 0, pl: 2.5 },
								'& code': {
									bgcolor: 'action.hover',
									borderRadius: 0.5,
									px: 0.5,
									fontSize: '0.85em',
								},
								// Fixed layout keeps tables inside the chat width — cells
								// wrap instead of pushing an inner horizontal scroll.
								'& table': {
									width: '100%',
									borderCollapse: 'collapse',
									tableLayout: 'fixed',
									my: 1,
									fontSize: '0.8125rem',
								},
								// Tables rise in once when they first appear — t-table-in is
								// only set on newly completed tables (see renderedHtml memo).
								'& table.t-table-in': {
									animation: 'assistantTableIn 350ms cubic-bezier(0.22, 1, 0.36, 1)',
								},
								'@keyframes assistantTableIn': {
									from: { opacity: 0, transform: 'translateY(16px) scale(0.97)', filter: 'blur(2px)' },
									to: { opacity: 1, transform: 'translateY(0) scale(1)', filter: 'blur(0)' },
								},
								'& th, & td': {
									border: '1px solid',
									borderColor: 'divider',
									px: 1,
									py: 0.5,
									textAlign: 'left',
									overflowWrap: 'anywhere',
								},
								'& th': {
									bgcolor: 'action.hover',
									fontWeight: 600,
								},
								'& table img': {
									width: ASSISTANT_TABLE_IMAGE_PX,
									height: ASSISTANT_TABLE_IMAGE_PX,
									objectFit: 'contain',
									display: 'block',
								},
								'& strong': { fontWeight: 600 },
								'& a': {
									color: 'primary.main',
									fontWeight: 500,
									textDecoration: 'underline',
									textUnderlineOffset: '2px',
								},
								// Streaming text: new words resolve through opacity + blur
								// on insertion; revealed words set animation:none inline.
								'& .t-stream-w': {
									animation: 'streamWordIn 350ms cubic-bezier(0.22, 1, 0.36, 1) both',
								},
								'@keyframes streamWordIn': {
									from: { opacity: 0, filter: 'blur(1px)' },
									to: { opacity: 1, filter: 'blur(0)' },
								},
								'@media (prefers-reduced-motion: reduce)': {
									'& .t-stream-w': { animation: 'none' },
									'& table.t-table-in': { animation: 'none' },
								},
								fontSize: '0.875rem',
								letterSpacing: '-0.02em',
							}}
						>
							{/* eslint-disable-next-line react/no-danger */}
							<div ref={contentRef} dangerouslySetInnerHTML={{ __html: renderedHtml }} onClick={handleContentClick} />
							{/* While streaming, narrate what's being built with the
								same ThinkingLine shimmer as the thinking indicator.
								Queued lines wait for the in-progress table — elements
								build in order, not at once. */}
							{message.isStreaming && (buildingTable || queuedNarrations.length > 0) ? (
								<Stack spacing={0.5} sx={{ fontSize: '0.8125rem', mt: 1 }}>
									{buildingTable ? <ThinkingLine key="building-table" text="Building table…" /> : null}
									{!buildingTable
										? queuedNarrations.map((text, i) => (
												<ThinkingLine key={`${i}-${text}`} delay={i * 450} text={text} />
											))
										: null}
								</Stack>
							) : null}
						</Box>
					)
				) : message.isStreaming ? (
					<ThinkingIndicator toolCalls={message.toolCalls} />
				) : null}

				{/* Charts, heatmaps, digests and tool widgets hold until the
					streamed text finishes, then rise in exactly once (see
					WidgetEntrance). */}
				{!message.isStreaming
					? widgetNodes.map((node) => (
							<WidgetEntrance key={node.key} seq={entranceSeqRef}>
								{node}
							</WidgetEntrance>
						))
					: null}

				{showPostContent && message.citations?.length ? <CitationChipList citations={message.citations} /> : null}

				{showPostContent && message.navigateActions?.length ? (
					<NavigateChipList actions={message.navigateActions} />
				) : null}

				{showPostContent && message.planogramDraftActions?.length ? (
					<Stack spacing={1} sx={{ mt: 1 }}>
						{message.planogramDraftActions.map((action) => (
							<Stack key={`${action.siteId}-${action.planogramLayoutId}`} spacing={0.75}>
								{action.appliedChanges?.length ? (
									<Box
										sx={{
											px: 1.25,
											py: 1,
											borderRadius: 1,
											bgcolor: 'action.hover',
											border: '1px solid',
											borderColor: 'divider',
										}}
									>
										<Typography sx={{ fontWeight: 600, mb: 1 }} variant="caption">
											Draft updated
										</Typography>
										<Stack spacing={1.5}>
											{action.appliedChanges.map((change) => (
												<ProductSwapCard
													fromName={change.from_product_name ?? change.from_recipe_name ?? null}
													fromPhoto={change.from_side_photo}
													key={`${change.cell_index}-${change.to_product_name ?? change.to_recipe_name}`}
													slotLabel={change.slot_label ?? `Cell ${change.cell_index}`}
													toName={change.to_product_name ?? change.to_recipe_name ?? null}
													toPhoto={change.to_side_photo}
												/>
											))}
										</Stack>
									</Box>
								) : null}
								<Chip
									component={RouterLink}
									clickable
									color="secondary"
									label={action.label}
									size="small"
									to={action.path}
									variant="filled"
									sx={{ alignSelf: 'flex-start' }}
								/>
							</Stack>
						))}
					</Stack>
				) : null}

				{showPostContent && message.promotionDraftActions?.length ? (
					<Stack spacing={1} sx={{ mt: 1 }}>
						{message.promotionDraftActions.map((draft, index) => (
							<PromotionFormWidget key={`${draft.product_name}-${index}`} draft={draft} />
						))}
					</Stack>
				) : null}

				{!isUser && message.dataSource && !message.isStreaming ? (
					<Typography color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.75rem' }} variant="caption">
						{formatDataSourceFooter(message.dataSource)}
					</Typography>
				) : null}
			</Box>

			{isUser && askedFromLabel ? (
				<Tooltip title={askedFromTruncated ? askedFromLabelRaw : ''}>
					<Box
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 0.5,
							color: 'text.secondary',
						}}
					>
						<MonitorIcon size={11} />
						<Typography sx={{ fontSize: '0.6875rem', lineHeight: 1.4 }} variant="caption">
							{askedFromLabel}
						</Typography>
					</Box>
				</Tooltip>
			) : null}

			{canRetry ? (
				<Tooltip title="Resend your last question without typing it again">
					<span>
						<Button
							disabled={isLoading}
							onClick={handleRetry}
							size="small"
							startIcon={<RetryIcon size={16} />}
							sx={{ ml: 0.5 }}
							variant="outlined"
						>
							Retry
						</Button>
					</span>
				</Tooltip>
			) : null}

			{showFeedback ? (
				<Stack spacing={0.5} sx={{ px: 0.5 }}>
					<Stack direction="row" spacing={0.5}>
						<Tooltip title={copied ? 'Copied!' : 'Copy'}>
							<IconButton
								aria-label="Copy message"
								color={copied ? 'success' : 'default'}
								onClick={handleCopy}
								size="small"
							>
								<SwapIcons
									active={copied}
									from={<CopyIcon size={16} />}
									to={<StrokeCheck on={copied} />}
									toHiddenTransform="scale(0.25) rotate(80deg)"
								/>
							</IconButton>
						</Tooltip>
						<Tooltip
							title={
								isSpeaking
									? 'Stop'
									: isSpeechLoading && speakingMessageId === message.id
										? 'Preparing audio…'
										: 'Read aloud'
							}
						>
							<IconButton
								aria-label={isSpeaking ? 'Stop speaking' : 'Read aloud'}
								color={isSpeaking ? 'primary' : 'default'}
								onClick={handleSpeak}
								size="small"
							>
								<SwapIcons active={isSpeaking} from={<SpeakerIcon size={16} />} to={<SpeakerStopIcon size={16} />} />
							</IconButton>
						</Tooltip>
						{hasTable ? (
							<Tooltip title="Export as CSV">
								<IconButton aria-label="Export as CSV" onClick={handleExportCsv} size="small">
									<DownloadIcon size={16} />
								</IconButton>
							</Tooltip>
						) : null}
						<Tooltip title="Helpful">
							<IconButton
								aria-label="Helpful response"
								color={message.feedbackRating === 1 ? 'primary' : 'default'}
								onClick={handleThumbsUp}
								size="small"
							>
								<SwapIcons
									active={message.feedbackRating === 1}
									from={<ThumbsUpIcon size={16} />}
									to={<ThumbsUpIcon size={16} weight="fill" />}
								/>
							</IconButton>
						</Tooltip>
						<Tooltip title="Not helpful">
							<IconButton
								aria-label="Unhelpful response"
								color={message.feedbackRating === -1 ? 'error' : 'default'}
								onClick={handleThumbsDown}
								size="small"
							>
								<SwapIcons
									active={message.feedbackRating === -1}
									from={<ThumbsDownIcon size={16} />}
									to={<ThumbsDownIcon size={16} weight="fill" />}
								/>
							</IconButton>
						</Tooltip>
					</Stack>
					{showReasonPicker && message.feedbackRating === -1 ? (
						<Stack direction="row" flexWrap="wrap" spacing={0.5}>
							{NEGATIVE_FEEDBACK_REASONS.map((reason) => (
								<Chip
									key={reason}
									clickable
									label={reason}
									onClick={() => handleReasonSelect(reason)}
									size="small"
									variant="outlined"
								/>
							))}
						</Stack>
					) : null}
				</Stack>
			) : null}
		</Stack>
	)
}
